import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { startCase } from 'lodash';
import { useMenu } from '../../hooks/useMenu';
import { APPLICANT_TYPES } from '../../util/constants';
import { useApplicantData } from '../../hooks/useApplicantData';
import { getApplicantData } from '../../util/helpers/applicantDataHelper';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import {
  ACCOUNT_TYPE,
  ACCOUNT_TYPE_GTM,
  IS_SALES_ASSESSTED_REGISTRATION_FLOW,
} from '../../constants/sessionStorage';
import OnBoardingContainer from '../../components/OnBoarding/OnBoardingContainer';
import { HeadingSubtitle } from '../../components/Heading';
import Paragraph from '../../components/Paragraph/Paragraph';
import ReviewData from '../../components/Review/ReviewData';
import OnBoardingCta from '../../components/OnBoarding/OnBoardingCta';
import AddressReviewFormat from '../../components/AddressReviewFormat';
import Modal from '../../components/Modal';
import BusinessEinForm from '../../components/Forms/BusinessEinForm';
import BusinessTypeForm from '../../components/Forms/BusinessTypeForm';
import AddressForm from '../../components/Forms/AddressForm';
import BusinessNameForm from '../../components/Forms/BusinessNameForm';
import { extractValue, hashedMask } from '../../util/helpers/einHelper';
import themeColors from '../../assets/styles/themeColors';
import MissingInfo from './MissingInfo';
import SaveAndCloseModal from '../../components/Modals/SaveAndCloseModal/SaveAndCloseModal';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  gap: 1.5rem;
`;

const BusinessReview = () => {
  const [isAddressOpen, setAddressOpen] = useState(false);
  const [isNameOpen, setNameOpen] = useState(false);
  const [isTypeOpen, setTypeOpen] = useState(false);
  const [isEinOpen, setEinOpen] = useState(false);
  const { t } = useTranslation();
  const { activeMenu, activeItem } = useMenu();
  const history = useHistory();
  const ApplicantData = useApplicantData(APPLICANT_TYPES.BUSINESS);
  const AccountType = retrieveFromSessionStorage(ACCOUNT_TYPE);
  const isSalesAssisted = retrieveFromSessionStorage(
    IS_SALES_ASSESSTED_REGISTRATION_FLOW,
  );
  const businessName = getApplicantData(ApplicantData, 'Name');
  const [eventType, setEventType] = useState('NoForm');
  const [saveAndCloseModalOpen, setSaveAndCloseModalOpen] = useState(false);

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Business Review',
      accountType: accountGtmType,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Business Review',
      accountType: accountGtmType,
    });
  };

  const formatEinNumber = (ein) => {
    if (isSalesAssisted && !ein) {
      return <MissingInfo />;
    }
    if (ein) {
      const formatedEin = hashedMask(extractValue(ein));
      return formatedEin;
    }
  };

  const onLooksGoodClick = () => {
    history.push(activeItem?.nextPath);
    handleGtmEvent();
  };

  return (
    <OnBoardingContainer
      activeMenu={activeMenu}
      activeItem={activeItem}
      previousPath={activeItem?.previousPath}
      eventType={eventType}
      setEventType={setEventType}
      setSaveAndCloseModalOpen={setSaveAndCloseModalOpen}
    >
      <HeadingSubtitle mb={12} color={themeColors.colorTextSecondary}>
        {t('verifyReview.title')}
      </HeadingSubtitle>
      <Paragraph mb={32} color={themeColors.colorTextXLLight}>
        {t('verifyReview.subtitle')}
      </Paragraph>
      <Grid>
        <ReviewData
          title={t('verifyReview.businessName')}
          onEdit={() => setNameOpen(true)}
          value={
            isSalesAssisted && businessName === '' ? (
              <MissingInfo />
            ) : (
              getApplicantData(ApplicantData, 'Name')
            )
          }
        />
        <ReviewData
          title={t('verifyReview.businessType')}
          onEdit={() => setTypeOpen(true)}
          value={
            isSalesAssisted && !AccountType ? (
              <MissingInfo />
            ) : (
              startCase(AccountType)
            )
          }
        />
        <ReviewData
          title={t('verifyReview.ein')}
          onEdit={() => setEinOpen(true)}
          value={
            formatEinNumber(getApplicantData(ApplicantData, 'EIN')) || 'N/A'
          }
        />
        <ReviewData
          title={t('verifyReview.address')}
          onEdit={() => setAddressOpen(true)}
          value={
            <AddressReviewFormat
              {...getApplicantData(ApplicantData, 'AddressResponse')}
              isSalesAssisted={isSalesAssisted}
            />
          }
        />
      </Grid>
      <OnBoardingCta
        title={t('looksGood')}
        onClick={onLooksGoodClick}
        buttonProps={{
          'data-cy': 'button-confirm',
        }}
      />
      <Modal
        isOpen={isEinOpen}
        onClose={() => setEinOpen(false)}
        label={t('verifyReview.businessEinLabel')}
      >
        <BusinessEinForm
          applicantType={APPLICANT_TYPES.BUSINESS}
          onModalClose={() => setEinOpen(false)}
          eventType={eventType}
          setEventType={setEventType}
        />
      </Modal>
      <Modal
        isOpen={isTypeOpen}
        onClose={() => setTypeOpen(false)}
        label={t('verifyReview.businessTypeLabel')}
      >
        <BusinessTypeForm
          applicantType={APPLICANT_TYPES.BUSINESS}
          onModalClose={() => setTypeOpen(false)}
          eventType={eventType}
          setEventType={setEventType}
        />
      </Modal>
      <Modal
        isOpen={isAddressOpen}
        onClose={() => setAddressOpen(false)}
        label={t('verifyReview.businessAddressLabel')}
      >
        <AddressForm
          applicantType={APPLICANT_TYPES.BUSINESS}
          onModalClose={() => setAddressOpen(false)}
          eventType={eventType}
          setEventType={setEventType}
        />
      </Modal>
      <Modal
        isOpen={isNameOpen}
        onClose={() => setNameOpen(false)}
        label={t('verifyReview.businessNameLabel')}
      >
        <BusinessNameForm
          applicantType={APPLICANT_TYPES.BUSINESS}
          onModalClose={() => setNameOpen(false)}
          eventType={eventType}
          setEventType={setEventType}
        />
      </Modal>
      {saveAndCloseModalOpen && (
        <SaveAndCloseModal
          isOpen={saveAndCloseModalOpen}
          onClose={() => setSaveAndCloseModalOpen(false)}
        />
      )}
    </OnBoardingContainer>
  );
};

export default BusinessReview;
